import { DEBUG } from '@src/DEBUG';
DEBUG.open ( '/xs/service/gis/color-management' );

import {Injectable} from '@angular/core';
import {Constants} from '../../../commons/Constants';

import { XsIdentityInterface } from '../../interfaces/identify.interface';
import { XsEventListenerInterface } from '../../interfaces/event-listener.interface';
import { Account, TransformGroup } from '4d-mapper';
import { User } from 'vasat';
import { XsApplicationEventsService } from '../events/application-events.service';
import {TranslateService} from '@ngx-translate/core';

import * as _ from 'lodash'

import rgbHex from 'rgb-hex';
import { R3BoundTarget } from '@angular/compiler';

const hexCharacters = 'a-f\\d';
const match3or4Hex = `#?[${hexCharacters}]{3}[${hexCharacters}]?`;
const match6or8Hex = `#?[${hexCharacters}]{6}([${hexCharacters}]{2})?`;
const nonHexChars = new RegExp(`[^#${hexCharacters}]`, 'gi');
const validHexSize = new RegExp(`^${match3or4Hex}$|^${match6or8Hex}$`, 'i');

export class XsPalette {
    id:string = '';
}

@Injectable({
    providedIn: 'root'
})
export class XsColorManagementService implements XsIdentityInterface, XsEventListenerInterface {

    __ID      = null;
    __EVENTS  = null;

    // for debugging
    debug:boolean = Constants.debug_off;

    palettes:any = {
        none: {
            label: 'None',
            colors: [
                ['None','#585756','#565758'],
            ]
        },
        default: {
            label: 'Standard',
            colors: [
                ['Blue','#0133FD','#3C33FF'],
                ['Light blue','#2C33FF','#33A8FF'],
                ['Teal','#32A8FF','#33F9FF'],
                ['Ghoul','#32F9FF','#33FFAF'],
                ['Green','#32FFAF','#5BFF33' ],
                ['Lime','#5AFF32','#ACFF33'],
                ['Yellow','#ACFF32','#FFFC33'],
                ['Orange','#FFFC32','#FF6B33'],
                ['Red','#FF6B32','#FF3300' ],
            ]
        },
        smallscale: {
            label: 'PositiveNegative',
            colors: [
                [ 'Negative', '#ff1100','#994400' ],
                [ 'Neutral', '#994400','#449900' ],
                [ 'Positive', '#449900','#11ff00' ],
            ]
        },
        flow: {
            label: 'Flow',
            colors: [
                [ 'Start', '#5588ff','#8899ff' ],
                [ 'Middle', '#8899ff','#44ffff' ],
                [ 'End', '#44ffff','#ffffff' ],
            ]
        },
        grayscale: {
            label: 'Grayscale',
            colors: [ 
                [ 'Dark','#000000','#666665' ],
                [ 'Middle','#666665','#aaaaa9' ],
                [ 'Bright','#aaaaa9','#ffffff' ],
            ]
        },
        stripe: {
            label: 'Stripe',
            colors: [ 
                [ 'Below','#001166','#00ddff', 48 ],
                [ 'Threshold','#00ddff','#ccbb00' ],
                [ 'Above','#ccbb00','#ff7700', 48 ],
            ]
        },
        split: {
            label: 'Split',
            colors: [ 
                [ 'Below','#001166','#002277',48 ],
                [ 'Threshold','#ffff33','#33ffff' ],
                [ 'Above','#002277','#001166',48 ],
            ]
        },
        soft: {
            label: 'Softly',
            colors: [
                [ 'Cherry','#9e0142','#d13b4b' ],
                [ 'Red','#d13b4b','#f0704a' ],
                [ 'Orange','#f0704a','#fcab63' ],
                [ 'Rust','#fcab63','#fedc8c' ],
                [ 'Mustard','#fedc8c','#fbf8b0' ],
                [ 'Yellow','#fbf8b0','#e0f3a1' ],
                [ 'Lime','#e0f3a1','#aadda2' ],
                [ 'Green','#aadda2','#69bda9' ],
                [ 'Teal','#69bda9','#4288b5' ],
                [ 'Blue','#4288b5','#9e0142' ],
            ]
        },
        topography: {
            label: 'Topography',
            colors: [
                [ 'Dark sea','#000022','#001166', 5 ],
                [ 'Sea','#001188','#0099ff', 25 ],
                [ 'Shore','#887722','#eeee00', 5 ],
                [ 'Seaside','#aaff11','#00dd22', 5 ],
                [ 'Land','#00dd22','#227700', 30 ],
                [ 'Mountains','#665500','#888888', 5 ],
                [ 'Snow','#eeeeff','#ffffff', 25 ],
            ]
        }
    }

    constructor (
        public applicationEvents: XsApplicationEventsService
    ) {

        this.__ID = XsIdentityInterface.__init(this);
        this.__EVENTS = XsEventListenerInterface.__init(this);

        DEBUG.log ( '/xs/service/gis/color-management' );
    }

    __config ( ret, config:any = {} ) {
        Object.keys(config).forEach(k => {
            ret[k] = config[k];
        });
        return ret;
    }

    listColorPalettes() {
        return Object.keys(this.palettes); // const defaults = [ 'default', 'grayscale', 'smallscale', 'soft', 'topography', 'stripe', 'split' ];
    }

    buildColorPercentages ( id:string = 'default' ) {

        const colors = this.palettes[id].colors.length;

        let foundPercentages = 0;

        this.palettes[id].colors.forEach( colorDefinition => {
            if ( Array.isArray(colorDefinition) ) {
                if ( colorDefinition[3] ) {
                    foundPercentages = foundPercentages + colorDefinition[3];
                }
            } else {
                // object definition; support coming
            }
        });
        this.palettes[id]['autoPercent'] = ( 100 - foundPercentages ) / colors;

        // console.log('!!! buildColorPercentages()', id, colors, foundPercentages, (100 - foundPercentages), this.palettes[id]['autoPercent'] );
    }

    getPaletteLut ( id:string = 'default' ) {
        let palette = this.palettes[id];
        if ( !palette ) {
            return null;
        }
        if ( palette.__colors_idx ) {
            return palette.__colors_idx;
        }
    }

    setPaletteLut ( id:string = 'default', lut:number[] = [] ) {
        let palette = this.palettes[id];
        if ( !palette ) {
            return null;
        }
        palette.__colors_idx = lut;
    }

    getPalette ( id:string = 'default' ) {

        let n = 0;
        let palette = this.palettes[id];
        if ( !palette ) {
            return { __debug_name:id, __colors:[], __colors_length:0, __colors_idx:[] };
        }

        if ( palette && !palette.__colors ) {

            palette.__colors = [];
            palette.__colors_info = [];
            palette.__colors_length = 0;
            palette.__colors_idx = [];

            let last_item = null;

            this.buildColorPercentages(id);

            palette.colors.forEach( colorDefinition => {
                if ( Array.isArray(colorDefinition) ) {

                    // array definition
                    // [ {label}, {from_color}, {to_color}, {percent:optional} ]

                    // for all colors that don't specify a percentage for their range, use the
                    // auto-calculated one (which takes the colors with specifics into account)

                    if ( !colorDefinition[3] ) {
                        colorDefinition[3] = this.palettes[id]['autoPercent'];
                    }

                    // convert percentages into actual colors
                    let segmentSize = colorDefinition[3] * 10;

                    // console.log('!!! getColors()', id, segmentSize, config.factor, colorDefinition );

                    // get the range of colors
                    let range = this.lerpColors( new Color(colorDefinition[1]), new Color(colorDefinition[2]), segmentSize, true, colorDefinition[0] );

                    // merge them into the result
                    range.forEach ( item => { 
                        palette.__colors[n] = item.value; 
                        palette.__colors_info[n] = item; 
                        palette.__colors_idx[n] = n;

                        last_item = item;

                        n++;
                    });

                } else {
                    // object definition; maybe support later
                }
            });

            // add one extra padding at the end (buffer safety)
            palette.__colors[n] = last_item.value;
            palette.__colors_info[n] = last_item;
            palette.__colors_idx[n] = n;

            palette.__colors_length = n;
        }
        return palette;
    }

    getColors ( id:string = 'default', incomingConfig:any = {} ) {

        let config = this.__config ({
            returnStructuredInfo: false,
            type:'number',
            min:0,
            max:100,
            items:0,
            factor: 100,
        }, incomingConfig );

        // no items? (resolution)
        if ( !config.items ) {
            if ( config.type == 'number' ) {
                // if ( config.max - config.min !== 100 ) {
                    config.items = config.max - config.min;
                // }
            } else {
                config.items = 100;
                config.min = 0;
                config.max = config.items;
            }
        }

        // palette definitions are in percentages, and output is in colors
        let palette = this.getPalette( id );

        // to return an array of colors, pick out items from the index object
        return this.idxObjectPickEvery( palette, config );

    }

    // pick from the incoming array spread out to [items] items
    idxObjectPickEvery( palette:any = {}, config:any = {} ) {

        let pickedItems:number = 100;
        if ( config['items'] ) {
            pickedItems = config['items'];
        }
        const totalItems = palette.__colors_length;
        const lut = palette.__colors_idx;
        let colors = palette.__colors;
        if ( config['returnStructuredInfo'] ) {
            colors = palette.__colors_info;
        }
        let ret = [];
        let debug = [];
        let factor = totalItems / pickedItems;

        if ( this.debug ) { console.log('!!! picking()', totalItems, pickedItems, factor, [ lut ] ); }
        
        for ( let n=0; n<pickedItems; n++ ) {
            const idx = n ? Math.floor((n+1) * factor) : 0;
            // if ( lut[idx] && colors[lut[idx]] ) {
                debug.push(n + '='+idx+'='+lut[idx]+' :: '+colors[idx]+'='+colors[lut[idx]] );
                ret.push( colors[lut[idx]] );
            // }
        }
        if ( this.debug ) { console.log('!!! picking() DONE', [ colors, ret ] ); }
        return ret;
    };

    /*
    getRangeNumberPercent(percent, min, max) {
        return min + (max - min) * percent / 100;
    }
    getRangeNumber(value, min, max) {
        let top = max - min;
        let factor = ( max - min ) / value;
        return value * factor;
    }
    clamp(value, min, max) {
        return Math.min(Math.max(value, min), max);
    }
    getMeters(idx, range ) {
        if ( !range.factor ) {
            range.factor = ( range.max - range.min ) / range.totalMax; 
        }
        let res = Math.round(( ((range.min + idx) * range.factor ) + Number.EPSILON) * 10000) / 10000;
        console.log("#########GET_METERS("+idx+")", res, range );
        return res;
    }
    getIndex(meter, range ) {
        const size = range.max - range.min ;
        if ( !range.factor ) {
            range.factor = size / range.totalMax; 
        }
        const res = Math.ceil( meter * range.factor );
        console.log("#########GET_INDEX("+meter+")", res, range );
        return res;
    }
    getScaledIndex ( idx, range ) {
        const size = range.max - range.min ;
        if ( !range.factor ) {
            range.factor = size / range.totalMax; 
        }
        const res = Math.ceil( idx * range.factor );
        // const res = Math.ceil((( ( idx * range.factor ) + Number.EPSILON) * 10000) / 10000);
        // console.log("#########GET_SCALED_INDEX("+idx+")", res );
        return res;
    }


    getScaled(idx, range ) {
        if ( !range.factor ) {
            range.factor = ( range.max - range.min ) / range.totalMax; 
        }
        let res = Math.round(( ((range.min + idx) * range.factor ) + Number.EPSILON) * 10000) / 10000;
        // console.log("#########GET_SCALED("+idx+")", res, range );
        return res;
    }
    */

    getScaledValue(value, range, floored:boolean = true ) {
        const targetRange = range.targetMax - range.targetMin;
        const sourceRange = range.sourceMax - range.sourceMin;
        const res = (value - range.sourceMin) * targetRange / sourceRange + range.targetMin;
        if ( floored ) {
            return Math.floor(res);
        }
        return res;
    }

    // creates a return array of Cesium rules for type 'cesium', and a string of linear-gradient for type 'css'
    renderColors ( colors:string[], type:string = 'cesium', props:any = {} ) { // min:number = 0, max:number = 1000, attribute:string = 'Height', opacity:number = 1, threshold:number = 50 ) {
        
        // let there be config!
        let config = this.__config({
            colorName: 'default',
            min: 0,
            max: 1000,
            attribute: 'Height',
            attributeType:'number',
            attributeLength:0,
            attributeMap:null,
            attributeRenderMode:'colormap', // 'colormap' || 'valuecheck'
            opacity: 1,
            threshold: 50,
            thresholdAbsolute: false,
            errorColor: "'red', 0.1",
            errorColorPoint: "'orange', 0.2",
            debug:false,
            rampWidth: 100,
            rampHeight: 1,
            bounds:false,
            boundsColor:null,
            boundsOpacity:1,
            boundsMinColor:null,
            boundsMaxColor:null
        }, props );

        // console.log('##render INIT', type, config, colors );

        // if colors isn't an array of colors (usually a type moniker, ie. 'default', 'posneg', etc.), get an array for that type
        if ( ! Array.isArray ( colors ) ) {
            colors = this.getColors ( colors );
            // console.log('##render INIT COLORS', colors );
        }

        let result = [];
        let ret = null;

        // handy shortcuts
        const propType = config.attributeType;
        const propMode = config.attributeRenderMode;
        const colorMapping = propMode !== 'valuecheck';

        
        let theseColors = _.clone(colors);
        let theseAttributes = [ config.attribute ];
        let valueKey = {};

        if ( type == 'cesium' && colorMapping ) {
            if ( config.attributeMap ) {
                let keys = Object.keys(config.attributeMap);
                let newcolors = [];
                let idx = 0;
                keys.forEach ( key => {
                    let c = config.attributeMap[key].color;
                    let cc = c ? c : 'rgb(50, 100, 200, '+config.opacity+')';
                    newcolors.push( cc );
                    valueKey[idx] = key;
                    idx++;
                });
                theseColors = newcolors;
            }
        }

        let finalColors = [];
        theseColors.forEach ( c => {
            if ( c ) {
                finalColors.push(this.convertColor(c, config.opacity) );
            }
        });

        let maxColorRules = finalColors.length;

        const isStriped = ( config.colorName == 'stripe' || config.colorName == 'split' ) ? true : false;

        if ( isStriped ) {

            let value = config.threshold;

            // default is percent
            let range = { sourceMin:0, sourceMax:100, targetMin:0, targetMax:maxColorRules };

            if ( config.thresholdAbsolute ) {
                range = { sourceMin:config.min, sourceMax:config.max, targetMin:0, targetMax:maxColorRules };
            }

            /* if ( value < range.sourceMin ) {
                value = range.sourceMin;
            }
            if ( value > range.sourceMax ) {
                value = range.sourceMax;
            } */
            let indexThreshold = this.getScaledValue ( value, range );


            const split = Math.ceil(colors.length / 2);
            let leftRange = { sourceMin:0, sourceMax:indexThreshold, targetMin:0, targetMax:split };
            let rightRange = { sourceMin:indexThreshold, sourceMax:maxColorRules, targetMin:split, targetMax:maxColorRules };
            
            /* console.log("#########THRESHOLD COLORS length", maxColorRules );
            console.log("#########THRESHOLD METERS min,value,max", config.min, config.threshold, value, config.max );
            console.log("#########THRESHOLD INDEX min,value,max", 0, indexThreshold, maxColorRules );
            console.log("#########THRESHOLD RANGES", range, leftRange, rightRange );
            console.log("#########THRESHOLD RANGES TEST LEFT", this.getScaledValue(0,leftRange), this.getScaledValue(indexThreshold,leftRange) );
            console.log("#########THRESHOLD RANGES TEST RIGHT", this.getScaledValue(indexThreshold,rightRange), this.getScaledValue(maxColorRules,rightRange) );
            */
            let newcolors = [];

            for ( let n=0; n<maxColorRules; n++ ){
                if ( n < indexThreshold ) {
                    let idx = this.getScaledValue ( n, leftRange ); // n ? Math.ceil( n * leftFactor ) : 0;
                    newcolors.push(finalColors[idx]); // newcolors.push('l_'+n+'_'+idx+'_'+colors[idx]);
                } else {
                    let idx = this.getScaledValue ( n, rightRange ); // n ? Math.ceil( n * rightFactor ) : 0;
                    newcolors.push(finalColors[idx]); // newcolors.push('r_'+n+'_'+idx+'_'+colors[idx]);
                }
                // console.log('#',n,)
            }

            // console.log("#########THRESHOLD DONE", newcolors.length, newcolors, colors );

            finalColors = newcolors;
        }


        let rgba = "rgba ( ${COLOR.r}*255, ${COLOR.g}*255, ${COLOR.b}*255, "+ config.opacity +")";
        let bMin = this.convertColor(config.boundsMinColor,config.opacity);
        let bMax = this.convertColor(config.boundsMaxColor,config.opacity);
        let bMinC = this.colorCesium(bMin);
        let bMaxC = this.colorCesium(bMax);
        // console.log("######### BOUNDS min", config.boundsMinColor, bMin, bMinC );
        // console.log("######### BOUNDS max", config.boundsMaxColor, bMax, bMaxC );

        switch ( type ) {

        case 'cesium-attribute':
        case 'cesium':
            // result.push ( ['${'+attribute+'} === null', 'color(\'yellow\', 0.4)'] );
            result.push ( ['${'+config.attribute+'} === undefined || ${'+config.attribute+'} === null', 'color('+config.errorColor+')'] );
            // result.push ( ['${COLOR} === undefined || ${COLOR.r} === undefined', 'color(yellow)'] );
            if ( propType == 'number' ) {
                result.push ( ['isNaN(${'+config.attribute+'}) || !isFinite(${'+config.attribute+'})', 'color('+config.errorColorPoint+')'] );
                if ( propMode == 'valuecheck' ) {
                    if ( config.bounds ) {
                        result.push ( ['${'+config.attribute+'} > ' + config.max, bMinC ] );
                        result.push ( ['${'+config.attribute+'} < ' + config.min, bMaxC ] );
                    } else {
                        result.push ( ['${'+config.attribute+'} > ' + config.max, rgba ] );
                        result.push ( ['${'+config.attribute+'} < ' + config.min, rgba ] );
                    }
                }
            }
            
            break;

        case 'cesium-pointcloud':
            // result.push ( ['${'+config.attribute+'} === undefined || ${'+config.attribute+'} === null || isNaN(${'+config.attribute+'}) || !isFinite(${'+config.attribute+'})', 'color('+config.errorColor+')'] );
            // result.push ( ['${'+config.attribute+'} > ' + config.max, rgba ] );
            // result.push ( ['${'+config.attribute+'} < ' + config.min, rgba ] );
            break;
        }

        

        
        // console.log("#########RENDER COLOR FIX", theseColors, finalColors );


        let attribute = theseAttributes[0];

        /* 
        snippet of code that would force Cesium to crash if you applied a grayscale to a pointcloud; used for debugging the Cesium crash detector, don't delete
        if ( config.colorName == 'grayscale' ) {
            result.push ( ['${BREAKING_BAD} === \' whatever \'', 'true' ] ); 
        } */

        for ( let n=0; n<maxColorRules; n++ ){
            
            switch ( type ) {

                case 'cesium-attribute': 
                case 'cesium': 
                    if ( colorMapping ) {
                        
                        // colormapping
                        let val = 'nothing'; // find colors[n] => property value

                        // lookup config.attributeMap
                        if ( config.attributeMap ) {
                            // attribute = theseAttributes[n];
                            val = valueKey[n];
                        }
                        if ( propType == 'string' ) {
                            result.push ( ['${'+attribute+'} === \'' +val+ '\'', this.colorCesium(finalColors[n]) ] ); 
                        } else if ( propType == 'number' ) {
                            result.push ( ['${'+attribute+'} === ' +val, this.colorCesium(finalColors[n]) ] ); 
                        }

                    } else {

                        let val = Math.round(( ( (config.min + n * (config.max - config.min) / maxColorRules) ) + Number.EPSILON) * 10000) / 10000;

                        //if ( config.opacity !== 1 ) {
                        //    result.push ( ['${'+attribute+'} < ' + val, 'color(\'#'+ theseColors[n] +'\', '+config.opacity+')'] ); 
                        //} else {
                            result.push ( ['${'+attribute+'} < ' + val, this.colorCesium(finalColors[n]) ] ); 
                        //}
                    }
                    break;
                    
                case 'cesium-pointcloud': 
                    break;

                case 'css': 
                case 'css-vertical': 
                result.push ( this.colorCss(finalColors[n]) ); break;

            }
        }
        
        let direction = '0deg';
        
        switch ( type ) {

            case 'cesium-attribute':
            case 'cesium-pointcloud':
            case 'cesium': 
                if ( propMode == 'valuecheck' ) {
                    result.push ( ['true', this.colorCesium(finalColors[maxColorRules-1]) ] );
                    /* if ( config.opacity !== 1 ) {
                        result.push ( ['true', 'color(\'#'+ theseColors[maxColorRules-1] +'\', '+config.opacity+')'] ); 
                    } else {
                        result.push ( ['true', 'color(\'#'+ theseColors[maxColorRules-1] +'\')'] ); 
                    } */
                } else {
                    result.push ( ['true', 'color(\'#ffffff\',\'0.2\')'] ); 
                }
                ret = result;
            break;

            case 'css': 
                direction = '90deg';
            case 'css-vertical': 
                result.push ( this.colorCss(finalColors[maxColorRules-1]) );
                //console.log('## CSS', result);
                ret = 'linear-gradient('+direction;
                result.forEach ( r => {
                    ret = ret + ','+ r;
                });
                ret = ret + ')';
            break;

            case 'image':

                config.rampWidth = maxColorRules;
                config.rampHeight = 1;

                var ramp = document.createElement('canvas');
                ramp.width = config.rampWidth;
                ramp.height = config.rampHeight;
                var ctx = ramp.getContext('2d');

                var grd = ctx.createLinearGradient(0, 0, config.rampWidth, config.rampHeight );

                //let grid = [];

                let minColor = this.colorCss(finalColors[0]); // this.makeColorTransparent ( '#'+finalColors[0], config.opacity );
                let maxColor = this.colorCss(finalColors[maxColorRules-1]); // this.makeColorTransparent ( '#'+finalColors[maxColorRules-1], config.opacity );

                if ( config.bounds ) {
                    minColor = config.boundsMinColor;
                    maxColor = config.boundsMaxColor;
                }

                const factor = 1 / maxColorRules;

                // console.log ( '!!! renderColors() IMG SETUP', { stops:maxColorRules,factor:factor, config:config, minColor:minColor, maxColor:maxColor } ); 

                grd.addColorStop ( 0, minColor );
                //grid.push('min-0-'+minColor);

                //console.log ('! '+0, maxColorRules, 0, colors[0] );
                for ( let n=1; n<maxColorRules; n++ ) {
                    grd.addColorStop ( n * factor, this.colorCss(finalColors[n]) ); // this.makeColorTransparent ( '#'+finalColors[n], config.opacity ) );
                }

                grd.addColorStop ( 1, maxColor );
                //grid.push('max-1-'+maxColor);

                ctx.fillStyle = grd;
                ctx.fillRect(0, 0, config.rampWidth, config.rampHeight);

                // console.log ( '!!! renderColors() IMG DONE', { stops:maxColorRules, colors:theseColors.length, grid:grid, config:config, grd:grd, ctx:ctx, ramp:ramp } ); 

                // ctx.strokeStyle="#FFFF0055";ctx.strokeRect(0, 0, config.rampWidth, config.rampHeight);

                ret = ramp;

            break;

        }

        // if ( this.debug ) { 
            // console.log ( '!!! renderColors()', { type:type, config:config, ret:ret, final:finalColors } ); 
        // }
        return ret;
    }

    convertColor ( c:any, opacity:number = 1 ) {
        const token:string = (''+c).trim().toLowerCase().substr(0,4);
        if ( token.substr(0,1) == '#' ) {
            return this.hexRgb ( c, { alpha:opacity } );
        } else if ( token !== 'rgba' && token !== 'rgb(' && token !== 'colo' ) {
            return this.hexRgb ( c, { alpha:opacity } );
        } else {
            let chunk = c.substring(c.indexOf('(')+1,c.indexOf(')'));
            let rgba = chunk.split(',');
            if ( token !== 'rgba' ) {
                rgba[3] = opacity;
            }
            return { red:rgba[0], green:rgba[1], blue:rgba[2], alpha:rgba[3], token:token, chunk:chunk };
        }
    }

    colorCesium ( rgba:any ) {
        if ( !rgba || !rgba.hasOwnProperty('red') || !rgba.hasOwnProperty('green') || !rgba.hasOwnProperty('blue') ) {
            // console.log('#',rgba);
            return "color('#ff6611',0.5)";
        }
        return "color('#"+this.numHex(rgba.red)+this.numHex(rgba.green)+this.numHex(rgba.blue)+"',"+rgba.alpha+")";
    }
    colorCss ( rgba:any ) {
        return "#"+this.numHex(rgba.red)+this.numHex(rgba.green)+this.numHex(rgba.blue)+this.numHex(rgba.alpha*255);
    }

    makeColorTransparent ( color:any = '#119955', opacity:number = 1 ) {
        let n = Math.round(opacity * 255);
        let h = this.numHex(n);
        return (color + h).slice(0,10);
    }
    
    numHex(s:any) {
        var a = parseInt(''+s).toString(16);
        if ((a.length % 2) > 0) {
            a = "0" + a;
        }
        return a;
    }

    lerp(a, b, fac) {
        var ret = [];
        for(var i = 0; i < Math.min(a.length, b.length); i++) {
            ret[i] = a[i] * (1 - fac) + b[i] * fac;
        }
        let c = new Color();
        c.setRGB(ret[0], ret[1], ret[2]);
        return c.toString();
    }
    
    lerpColors(begin, end, n, createLegend:any = null, legend:string = null ) {
        var ret = [];
        let id = this.uuidv4();
        for(var i = 0; i < n; i++) {
            var fac = i / (n - 1);
            let c = this.lerp(begin.toRGBArray(), end.toRGBArray(), fac);
            if ( createLegend ) {
                ret.push( { id:id, idx:i, n:n, fac:fac, legend:legend, value:c } );
            } else {
                ret.push( '#' + c );
            }
        }
        return ret;
    }
    
    uuidv4() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    reverseArray(array) {
        return array.reverse();
    }
    
    // randomize an array
    randomizeArray(array) {
        var currentIndex = array.length, temporaryValue, randomIndex;
    
        // While there remain elements to shuffle...
        while (0 !== currentIndex) {
        
            // Pick a remaining element...
            randomIndex = Math.floor(Math.random() * currentIndex);
            currentIndex -= 1;
        
            // And swap it with the current element.
            temporaryValue = array[currentIndex];
            array[currentIndex] = array[randomIndex];
            array[randomIndex] = temporaryValue;
        }
    
        return array;
    }
    
    hexRgb(hex, options:any = { format:'css' }) {
        if (typeof hex !== 'string' || nonHexChars.test(hex) || !validHexSize.test(hex)) {
            console.warn('Color error:expected hex, got:',hex);
            hex = '#ffcc45';
            // throw new TypeError('Expected a valid hex string');
        }
    
        hex = hex.replace(/^#/, '');
        let alphaFromHex = 1;
    
        if (hex.length === 8) {
            alphaFromHex = Number.parseInt(hex.slice(6, 8), 16) / 255;
            hex = hex.slice(0, 6);
        }
    
        if (hex.length === 4) {
            alphaFromHex = Number.parseInt(hex.slice(3, 4).repeat(2), 16) / 255;
            hex = hex.slice(0, 3);
        }
    
        if (hex.length === 3) {
            hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        }
    
        const number = Number.parseInt(hex, 16);
        const red = number >> 16;
        const green = (number >> 8) & 255;
        const blue = number & 255;
        const alpha = typeof options['alpha'] === 'number' ? options['alpha'] : alphaFromHex;
    
        if (options['format'] === 'array') {
            return [red, green, blue, alpha];
        }
    
        if (options['format'] === 'css') {
            // const alphaString = alpha === 1 ? '' : `, ${Number((alpha * 100).toFixed(2))}%`;
            return `rgba(${red},${green},${blue},${alpha})`;
        }
    
        return {red, green, blue, alpha, hex, number};
    }
}





/**
 * A class to parse color values
 * @author Stoyan Stefanov <sstoo@gmail.com>
 * @link   http://www.phpied.com/rgb-color-parser-in-javascript/
 * @license Use it if you like it
 */
export class Color {

r:any;
g:any;
b:any;
h:any;
v:any;
s:any;

static simple_colors = {
    aliceblue: 'f0f8ff',
    antiquewhite: 'faebd7',
    aqua: '00ffff',
    aquamarine: '7fffd4',
    azure: 'f0ffff',
    beige: 'f5f5dc',
    bisque: 'ffe4c4',
    black: '000000',
    blanchedalmond: 'ffebcd',
    blue: '0000ff',
    blueviolet: '8a2be2',
    brown: 'a52a2a',
    burlywood: 'deb887',
    cadetblue: '5f9ea0',
    chartreuse: '7fff00',
    chocolate: 'd2691e',
    coral: 'ff7f50',
    cornflowerblue: '6495ed',
    cornsilk: 'fff8dc',
    crimson: 'dc143c',
    cyan: '00ffff',
    darkblue: '00008b',
    darkcyan: '008b8b',
    darkgoldenrod: 'b8860b',
    darkgray: 'a9a9a9',
    darkgreen: '006400',
    darkkhaki: 'bdb76b',
    darkmagenta: '8b008b',
    darkolivegreen: '556b2f',
    darkorange: 'ff8c00',
    darkorchid: '9932cc',
    darkred: '8b0000',
    darksalmon: 'e9967a',
    darkseagreen: '8fbc8f',
    darkslateblue: '483d8b',
    darkslategray: '2f4f4f',
    darkturquoise: '00ced1',
    darkviolet: '9400d3',
    deeppink: 'ff1493',
    deepskyblue: '00bfff',
    dimgray: '696969',
    dodgerblue: '1e90ff',
    feldspar: 'd19275',
    firebrick: 'b22222',
    floralwhite: 'fffaf0',
    forestgreen: '228b22',
    fuchsia: 'ff00ff',
    gainsboro: 'dcdcdc',
    ghostwhite: 'f8f8ff',
    gold: 'ffd700',
    goldenrod: 'daa520',
    gray: '808080',
    green: '008000',
    greenyellow: 'adff2f',
    honeydew: 'f0fff0',
    hotpink: 'ff69b4',
    indianred : 'cd5c5c',
    indigo : '4b0082',
    ivory: 'fffff0',
    khaki: 'f0e68c',
    lavender: 'e6e6fa',
    lavenderblush: 'fff0f5',
    lawngreen: '7cfc00',
    lemonchiffon: 'fffacd',
    lightblue: 'add8e6',
    lightcoral: 'f08080',
    lightcyan: 'e0ffff',
    lightgoldenrodyellow: 'fafad2',
    lightgrey: 'd3d3d3',
    lightgreen: '90ee90',
    lightpink: 'ffb6c1',
    lightsalmon: 'ffa07a',
    lightseagreen: '20b2aa',
    lightskyblue: '87cefa',
    lightslateblue: '8470ff',
    lightslategray: '778899',
    lightsteelblue: 'b0c4de',
    lightyellow: 'ffffe0',
    lime: '00ff00',
    limegreen: '32cd32',
    linen: 'faf0e6',
    magenta: 'ff00ff',
    maroon: '800000',
    mediumaquamarine: '66cdaa',
    mediumblue: '0000cd',
    mediumorchid: 'ba55d3',
    mediumpurple: '9370d8',
    mediumseagreen: '3cb371',
    mediumslateblue: '7b68ee',
    mediumspringgreen: '00fa9a',
    mediumturquoise: '48d1cc',
    mediumvioletred: 'c71585',
    midnightblue: '191970',
    mintcream: 'f5fffa',
    mistyrose: 'ffe4e1',
    moccasin: 'ffe4b5',
    navajowhite: 'ffdead',
    navy: '000080',
    oldlace: 'fdf5e6',
    olive: '808000',
    olivedrab: '6b8e23',
    orange: 'ffa500',
    orangered: 'ff4500',
    orchid: 'da70d6',
    palegoldenrod: 'eee8aa',
    palegreen: '98fb98',
    paleturquoise: 'afeeee',
    palevioletred: 'd87093',
    papayawhip: 'ffefd5',
    peachpuff: 'ffdab9',
    peru: 'cd853f',
    pink: 'ffc0cb',
    plum: 'dda0dd',
    powderblue: 'b0e0e6',
    purple: '800080',
    red: 'ff0000',
    rosybrown: 'bc8f8f',
    royalblue: '4169e1',
    saddlebrown: '8b4513',
    salmon: 'fa8072',
    sandybrown: 'f4a460',
    seagreen: '2e8b57',
    seashell: 'fff5ee',
    sienna: 'a0522d',
    silver: 'c0c0c0',
    skyblue: '87ceeb',
    slateblue: '6a5acd',
    slategray: '708090',
    snow: 'fffafa',
    springgreen: '00ff7f',
    steelblue: '4682b4',
    tan: 'd2b48c',
    teal: '008080',
    thistle: 'd8bfd8',
    tomato: 'ff6347',
    turquoise: '40e0d0',
    violet: 'ee82ee',
    violetred: 'd02090',
    wheat: 'f5deb3',
    white: 'ffffff',
    whitesmoke: 'f5f5f5',
    yellow: 'ffff00',
    yellowgreen: '9acd32'
};

constructor (colorString:string = 'black' ) {

    // strip any leading #
    if (colorString.charAt(0) == '#') { // remove # if any
        colorString = colorString.substr(1,6);
    }

    colorString = colorString.replace(/ /g,'');
    colorString = colorString.toLowerCase();

    // before getting into regexps, try simple matches
    // and overwrite the input
    
    if ( Color[colorString] ) {
        colorString = Color[colorString];
    }

    // emd of simple type-in colors

    // array of color definition objects
    var color_defs = [
        {
            re: /^rgb\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3})\)$/,
            example: ['rgb(123, 234, 45)', 'rgb(255,234,245)'],
            process: function (bits){
                return [
                    parseInt(bits[1]),
                    parseInt(bits[2]),
                    parseInt(bits[3])
                ];
            }
        },
        {
            re: /^(\w{2})(\w{2})(\w{2})$/,
            example: ['#00ff00', '336699'],
            process: function (bits){
                return [
                    parseInt(bits[1], 16),
                    parseInt(bits[2], 16),
                    parseInt(bits[3], 16)
                ];
            }
        },
        {
            re: /^(\w{1})(\w{1})(\w{1})$/,
            example: ['#fb0', 'f0f'],
            process: function (bits){
                return [
                    parseInt(bits[1] + bits[1], 16),
                    parseInt(bits[2] + bits[2], 16),
                    parseInt(bits[3] + bits[3], 16)
                ];
            }
        }
    ];

    // search through the definitions to find a match
    for (var i = 0; i < color_defs.length; i++) {
        var re = color_defs[i].re;
        var processor = color_defs[i].process;
        var bits = re.exec(colorString);
        if (bits) {
            var channels = processor(bits);
            this.r = channels[0];
            this.g = channels[1];
            this.b = channels[2];
        }

    }

    // validate/cleanup values
    this.r = (this.r < 0 || isNaN(this.r)) ? 0 : ((this.r > 255) ? 255 : this.r);
    this.g = (this.g < 0 || isNaN(this.g)) ? 0 : ((this.g > 255) ? 255 : this.g);
    this.b = (this.b < 0 || isNaN(this.b)) ? 0 : ((this.b > 255) ? 255 : this.b);

    // set up initial hsv
    this.setRGB(this.r, this.g, this.b);
}

setRGB (r, g, b) { // null = don't change
    var hsv = this.RGB_HSV(
        r==null ? this.r : (this.r = r),
        g==null ? this.g : (this.g = g),
        b==null ? this.b : (this.b = b)
    );

    if (hsv[0] != null) {
        this.h = hsv[0];
    }

    if (hsv[2] != 0) {
        this.s = hsv[1];
    }

    this.v = hsv[2];
}

setHSV (h, s, v) { // null = don't change
    var rgb = this.HSV_RGB(
        h==null ? this.h : (this.h=h),
        s==null ? this.s : (this.s=s),
        v==null ? this.v : (this.v=v)
    );

    this.r = rgb[0];
    this.g = rgb[1];
    this.b = rgb[2];
}

RGB_HSV (r, g, b) {
    var n = Math.min(Math.min(r, g), b);
    var v = Math.max(Math.max(r, g), b);
    var m = v - n;

    if(m == 0) {
        return [ null, 0, v ];
    }

    var h = r==n ? 3 + (b - g) / m : (g == n ? 5 + (r - b) / m : 1 + (g - r) / m);

    return [ h == 6? 0: h, m / v, v ];
}

HSV_RGB (h, s, v) {
    if (h == null) {
        return [ v, v, v ];
    }

    var i = Math.floor(h);
    var f = i % 2 ? h - i : 1 - (h - i);
    var m = v * (1 - s);
    var n = v * (1 - s * f);

    switch(i) {
        case 6:
        case 0:
            return [ v, n, m ];
        case 1:
            return [ n, v, m ];
        case 2:
            return [ m, v, n ];
        case 3:
            return [ m, n, v ];
        case 4:
            return [ n, m, v ];
        case 5:
            return [ v, m, n ];
    }
}

toString () {
    var r = Math.round(this.r).toString(16);
    var g = Math.round(this.g).toString(16);
    var b = Math.round(this.b).toString(16);

    return (
        (r.length == 1 ? '0' + r : r) +
        (g.length == 1 ? '0' + g : g) +
        (b.length == 1 ? '0' + b : b)
        ).toUpperCase();
}

toRGBArray () {
    return [this.r, this.g, this.b];
}

toHSVArray() {
    return [this.h, this.s, this.v];
}
}